import { pnum } from "../converters/numberInput";
import { useMemo, useState, useEffect, useRef } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, X, CheckCircle2 } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { findMetroMatch, findMetroByZip, type MetroColData } from "@/lib/metroCostOfLivingData";
import { Link } from "wouter";
import { trackToolCompleted } from "@/lib/analytics";
import { NoticeBox } from "@/components/NoticeBox";

interface Category {
  id: number;
  name: string;
  current: string;
  new_: string;
}

// A single, real national-average baseline -- not two different example
// numbers -- since this now gets scaled independently per city by that
// city's own cost-of-living index. Grounded in the 2024 BLS Consumer
// Expenditure Survey (average household: $26,266/yr shelter, food,
// transportation, etc.), rounded to sensible whole numbers rather than
// claiming false precision from imperfectly-matched BLS subcategories.
const DEFAULT_CATEGORIES: Category[] = [
  { id: 1, name: "Rent / Mortgage", current: "1400", new_: "1400" },
  { id: 2, name: "Groceries", current: "500", new_: "500" },
  { id: 3, name: "Utilities", current: "200", new_: "200" },
  { id: 4, name: "Transportation", current: "350", new_: "350" },
  { id: 5, name: "Insurance", current: "160", new_: "160" },
  { id: 6, name: "Healthcare", current: "200", new_: "200" },
  { id: 7, name: "Entertainment", current: "250", new_: "250" },
];
let nextCategoryId = 8;

const TOOL_INFO_CONTENT = {
  what: `Compares your actual monthly expenses against a new city, then calculates the difference and the equivalent salary needed to maintain the same standard of living. When you enter a recognized major U.S. metro area (by city name or ZIP code), groceries, utilities, transportation and other everyday categories are auto-filled using real government cost-of-living data (rent is left for you to enter, since the all-items index misstates housing), and a state-level tax and housing comparison appears below — everything stays fully editable, so you can adjust any number to match your actual situation.`,
  how: `Type a current and new city, or their ZIP codes if the city name doesn't match directly (useful for smaller towns not on our list of 127 covered metros — the ZIP resolves to the nearest one). Category estimates and a state tax/housing comparison auto-fill from real data, adjustable to match your own research, then enter your salary to see the equivalent needed in the new city.`,
  tip: `Auto-filled estimates come from the U.S. Bureau of Economic Analysis's Regional Price Parities (2023, the most recent available) — a real government cost-of-living index, not a crowdsourced or made-up number, but still a metro-area-wide average, not neighborhood-specific, and about a year old like every free cost-of-living source. State tax and property figures come from Tax Foundation and Census Bureau data, and are state-level averages, not metro-specific — property tax rates and home values in particular can vary a lot by county. Treat all of this as a solid starting point, then adjust with real listings and your specific target county for anything that matters to your decision.`,
};

export default function CostOfLivingComparison() {
  const [currentCity, setCurrentCity] = useState("Current City");
  const [newCity, setNewCity] = useState("New City");
  const [currentZip, setCurrentZip] = useState("");
  const [newZip, setNewZip] = useState("");
  const [categories, setCategories] = useState<Category[]>(DEFAULT_CATEGORIES);
  const [currentSalary, setCurrentSalary] = useState("70000");
  const [appliedCurrentMetro, setAppliedCurrentMetro] = useState<MetroColData | null>(null);
  const [appliedNewMetro, setAppliedNewMetro] = useState<MetroColData | null>(null);

  // ZIP code takes priority when provided -- it's unambiguous (no "which
  // Morrisville" problem the way a typed city name can be), falling back
  // to city-name matching only when no ZIP is entered.
  const currentMetroMatch = useMemo(
    () => (currentZip.trim() ? (currentZip.length === 5 ? findMetroByZip(currentZip) : null) : findMetroMatch(currentCity)),
    [currentCity, currentZip]
  );
  const newMetroMatch = useMemo(
    () => (newZip.trim() ? (newZip.length === 5 ? findMetroByZip(newZip) : null) : findMetroMatch(newCity)),
    [newCity, newZip]
  );

  // Auto-fills each column independently the moment its city resolves to
  // a known metro, scaling the baseline national-average numbers by that
  // metro's real cost-of-living index -- re-applies if the matched city
  // itself changes, but never touches the other column, and never
  // overwrites a column whose city doesn't match anything recognized (so
  // a typo or an unlisted city just leaves manual entry exactly as it
  // was before this feature existed).
  useEffect(() => {
    if (!currentMetroMatch || currentMetroMatch === appliedCurrentMetro) return;
    const scale = currentMetroMatch.index / 100;
    setCategories(prev =>
      prev.map(c => {
        const baseline = DEFAULT_CATEGORIES.find(d => d.name === c.name);
        if (!baseline || baseline.name === "Rent / Mortgage") return c; // housing is not scaled by the all-items index
        return { ...c, current: String(Math.round(Number(baseline.current) * scale)) };
      })
    );
    setAppliedCurrentMetro(currentMetroMatch);
  }, [currentMetroMatch, appliedCurrentMetro]);

  useEffect(() => {
    if (!newMetroMatch || newMetroMatch === appliedNewMetro) return;
    const scale = newMetroMatch.index / 100;
    setCategories(prev =>
      prev.map(c => {
        const baseline = DEFAULT_CATEGORIES.find(d => d.name === c.name);
        if (!baseline || baseline.name === "Rent / Mortgage") return c;
        return { ...c, new_: String(Math.round(Number(baseline.new_) * scale)) };
      })
    );
    setAppliedNewMetro(newMetroMatch);
  }, [newMetroMatch, appliedNewMetro]);

  // This tool has no discrete "submit" action -- it's reactive, updating
  // as you type. The genuine "successful use" moment is the first time
  // BOTH cities resolve to a recognized metro and a real comparison
  // becomes possible, not every keystroke along the way. useRef (not
  // state) so this fires exactly once per successful match, without
  // itself triggering a re-render.
  const hasTrackedCompletion = useRef(false);
  useEffect(() => {
    if (currentMetroMatch && newMetroMatch && !hasTrackedCompletion.current) {
      trackToolCompleted("cost-of-living-comparison", "relocation");
      hasTrackedCompletion.current = true;
    }
  }, [currentMetroMatch, newMetroMatch]);

  const update = (id: number, field: "current" | "new_", value: string) => {
    setCategories(prev =>
      prev.map(c => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const renameCategory = (id: number, name: string) => {
    setCategories(prev => prev.map(c => (c.id === id ? { ...c, name } : c)));
  };

  const addCategory = () => {
    setCategories(prev => [
      ...prev,
      { id: nextCategoryId++, name: "New Category", current: "0", new_: "0" },
    ]);
  };

  const removeCategory = (id: number) => {
    setCategories(prev => prev.filter(c => c.id !== id));
  };

  const totals = useMemo(() => {
    const currentTotal = categories.reduce(
      (sum, c) => sum + (pnum(c.current) || 0),
      0
    );
    const newTotal = categories.reduce(
      (sum, c) => sum + (pnum(c.new_) || 0),
      0
    );
    const diff = newTotal - currentTotal;
    const diffPct = currentTotal !== 0 ? (diff / currentTotal) * 100 : 0;

    // salary needed to maintain the same standard of living
    const salary = pnum(currentSalary || "0");
    const equivalentSalary =
      currentTotal !== 0 ? salary * (newTotal / currentTotal) : salary;

    return { currentTotal, newTotal, diff, diffPct, equivalentSalary };
  }, [categories, currentSalary]);

  // No export/download/copy anywhere in this tool -- purely a
  // local calculator with nothing generated or delivered, same
  // reasoning as Government Forms Hub: the hook here was a
  // misapplication, not a gating gap.
  return (
    <ToolContainer>
      <div className="grid grid-cols-2 gap-4 mb-2">
        <div>
          <Label className="mb-1 block text-sm">Current City</Label>
          <Input aria-label="Current City"
            value={currentCity}
            onChange={e => setCurrentCity(e.target.value)}
          />
          <Input
            aria-label="Current ZIP code"
            value={currentZip}
            onChange={e => setCurrentZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
            placeholder="or enter ZIP code"
            inputMode="numeric"
            className="mt-1.5 text-xs h-8"
          />
          {currentZip && (
            currentZip.length < 5 ? (
              <p className="text-xs text-muted-foreground mt-1">Enter all 5 digits of the ZIP code.</p>
            ) : !currentMetroMatch ? (
              <p role="alert" className="text-xs text-destructive mt-1">ZIP {currentZip} is not in our table, so nothing was filled. Enter your numbers or try a city name.</p>
            ) : (
              <NoticeBox className="text-xs mt-1">ZIP {currentZip} is matched by its first 3 digits to the nearest covered metro: {currentMetroMatch.metro}. That is an approximation, so check it is the area you mean.</NoticeBox>
            )
          )}
          {currentMetroMatch && (
            <p className="text-xs text-primary flex items-center gap-1 mt-1">
              <CheckCircle2 className="h-3 w-3 shrink-0" /> Auto-filled using {currentMetroMatch.metro} data (BEA, 2023)
            </p>
          )}
        </div>
        <div>
          <Label className="mb-1 block text-sm">New City</Label>
          <Input aria-label="New City" value={newCity} onChange={e => setNewCity(e.target.value)} />
          <Input
            aria-label="New ZIP code"
            value={newZip}
            onChange={e => setNewZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
            placeholder="or enter ZIP code"
            inputMode="numeric"
            className="mt-1.5 text-xs h-8"
          />
          {newZip && (
            newZip.length < 5 ? (
              <p className="text-xs text-muted-foreground mt-1">Enter all 5 digits of the ZIP code.</p>
            ) : !newMetroMatch ? (
              <p role="alert" className="text-xs text-destructive mt-1">ZIP {newZip} is not in our table, so nothing was filled. Enter your numbers or try a city name.</p>
            ) : (
              <NoticeBox className="text-xs mt-1">ZIP {newZip} is matched by its first 3 digits to the nearest covered metro: {newMetroMatch.metro}. That is an approximation, so check it is the area you mean.</NoticeBox>
            )
          )}
          {newMetroMatch && (
            <p className="text-xs text-primary flex items-center gap-1 mt-1">
              <CheckCircle2 className="h-3 w-3 shrink-0" /> Auto-filled using {newMetroMatch.metro} data (BEA, 2023)
            </p>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        {currentMetroMatch || newMetroMatch
          ? "Estimates below were auto-filled from real government cost-of-living data for the recognized city (or cities) above — every number is still fully editable, so adjust anything to match your actual costs or research."
          : "Type a major U.S. city above (or its ZIP code, if the city name doesn't match — useful for smaller towns not directly on our list, since it resolves to the nearest of the 127 metros we cover) to auto-fill estimates from real cost-of-living data, or enter your own numbers directly below."}
      </p>

      {currentMetroMatch && newMetroMatch && Math.abs(currentMetroMatch.index - newMetroMatch.index) < 1 && (
        <p className="text-xs text-muted-foreground mb-4 border-l-2 border-primary/40 pl-2">
          {currentMetroMatch.metro} and {newMetroMatch.metro} have nearly identical overall
          cost-of-living index scores in the 2023 BEA data ({currentMetroMatch.index} vs.{" "}
          {newMetroMatch.index}) — that's a real result, not an error, and it's why the estimates
          below came out so close to each other. The overall index can still hide real differences
          in specific categories like rent, so it's worth double-checking those individually.
        </p>
      )}

      <div className="space-y-2 mb-6">
        <div className="grid grid-cols-[1fr_120px_120px_32px] gap-2 text-xs font-medium text-muted-foreground px-1">
          <span>Category</span>
          <span>{currentCity}</span>
          <span>{newCity}</span>
          <span></span>
        </div>
        {(currentMetroMatch || newMetroMatch) && (
          <NoticeBox className="text-xs mb-2" testId="col-rent-note">
            Rent / Mortgage is not auto-filled: the all-items index understates how much housing differs between cities. Enter your real rent or a current listing price.
            {currentMetroMatch && newMetroMatch && ` Typical home value for reference: ${currentMetroMatch.state} $${currentMetroMatch.medianHomeValue.toLocaleString()} vs ${newMetroMatch.state} $${newMetroMatch.medianHomeValue.toLocaleString()} (state medians).`}
          </NoticeBox>
        )}
        {categories.map(c => (
          <div
            key={c.id}
            className="grid grid-cols-[1fr_120px_120px_32px] gap-2 items-center"
          >
            <Input
              aria-label={`Category name: ${c.name}`}
              value={c.name}
              onChange={e => renameCategory(c.id, e.target.value)}
              className="text-sm h-9"
            />
            <Input
              aria-label={`${c.name}: current monthly cost`}
              value={c.current}
              onChange={e => update(c.id, "current", e.target.value)}
            />
            <Input
              aria-label={`${c.name}: new monthly cost`}
              value={c.new_}
              onChange={e => update(c.id, "new_", e.target.value)}
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-9 w-9"
              onClick={() => removeCategory(c.id)}
              title="Remove this category"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addCategory} className="gap-1.5 mt-1">
          <Plus className="h-3.5 w-3.5" /> Add Category
        </Button>
      </div>

      <div>
        <Label className="mb-1 block text-sm">
          Current Annual Salary ($, optional)
        </Label>
        <Input
          aria-label="Current annual salary ($, optional)"
          value={currentSalary}
          onChange={e => setCurrentSalary(e.target.value)}
          className="max-w-xs"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Card className="p-4 text-center">
          <p className="text-lg font-bold">
            ${totals.currentTotal.toLocaleString()}/mo
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {currentCity} Total
          </p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-lg font-bold">
            ${totals.newTotal.toLocaleString()}/mo
          </p>
          <p className="text-xs text-muted-foreground mt-1">{newCity} Total</p>
        </Card>
        <Card className="p-4 text-center">
          <p
            className={`text-lg font-bold ${totals.diff >= 0 ? "text-red-600" : "text-green-600"}`}
          >
            {totals.diff >= 0 ? "+" : ""}
            {totals.diffPct.toFixed(1)}%
          </p>
          <p className="text-xs text-muted-foreground mt-1">Difference</p>
          <span className="inline-block mt-1.5 rounded bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 px-2 py-0.5 text-xs font-semibold" data-testid="excl-housing-badge">Excludes housing unless you enter rent</span>
        </Card>
      </div>

      {pnum(currentSalary || "0") > 0 && (
        <Card className="mt-4 p-4 text-center border-primary/40">
          <p className="text-sm text-muted-foreground">
            To maintain the same standard of living in {newCity}, you'd need
            roughly
          </p>
          <p className="text-2xl font-bold text-primary mt-1">
            $
            {totals.equivalentSalary.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })}
            /year
          </p>
          <span className="inline-block mt-1.5 rounded bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 px-2 py-0.5 text-xs font-semibold">Excludes housing unless you enter rent</span>
        </Card>
      )}

      {currentMetroMatch && newMetroMatch && (
        <div className="mt-6">
          <p className="text-sm font-medium mb-1">State-Level Taxes &amp; Housing</p>
          <p className="text-xs text-muted-foreground mb-3">
            These four figures are state-level, not metro-specific — property tax and home
            value in particular vary a lot by county within a state, so treat these as a
            starting point, not a substitute for checking your actual target county. The top
            rate is not what a typical income pays; for the tax at your salary use the{" "}
            <Link href="/tool/state-tax-calculator" className="underline text-primary">state income tax calculator</Link>.
          </p>
          <div className="grid grid-cols-[1fr_120px_120px] gap-2 text-xs font-medium text-muted-foreground px-1 mb-1">
            <span>Category</span>
            <span>{currentMetroMatch.state}</span>
            <span>{newMetroMatch.state}</span>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="grid grid-cols-[1fr_120px_120px] gap-2 items-center py-1.5 border-b">
              <span className="text-muted-foreground">Top income tax rate</span>
              <span>{currentMetroMatch.incomeTaxTopRate === 0 ? "No income tax" : `${currentMetroMatch.incomeTaxTopRate}%`}</span>
              <span>{newMetroMatch.incomeTaxTopRate === 0 ? "No income tax" : `${newMetroMatch.incomeTaxTopRate}%`}</span>
            </div>
            <div className="grid grid-cols-[1fr_120px_120px] gap-2 items-center py-1.5 border-b">
              <span className="text-muted-foreground">Combined sales tax</span>
              <span>{currentMetroMatch.salesTaxCombined === 0 ? "No sales tax" : `${currentMetroMatch.salesTaxCombined}%`}</span>
              <span>{newMetroMatch.salesTaxCombined === 0 ? "No sales tax" : `${newMetroMatch.salesTaxCombined}%`}</span>
            </div>
            <div className="grid grid-cols-[1fr_120px_120px] gap-2 items-center py-1.5 border-b">
              <span className="text-muted-foreground">Property tax rate</span>
              <span>{currentMetroMatch.propertyTaxRate}%</span>
              <span>{newMetroMatch.propertyTaxRate}%</span>
            </div>
            <div className="grid grid-cols-[1fr_120px_120px] gap-2 items-center py-1.5">
              <span className="text-muted-foreground">Median home value</span>
              <span>${currentMetroMatch.medianHomeValue.toLocaleString()}</span>
              <span>${newMetroMatch.medianHomeValue.toLocaleString()}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Source: Tax Foundation 2026 state tax data (income and sales tax) and Census Bureau
            ACS 2024 estimates via Tax Foundation (property tax and home value).
          </p>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
